import React, { useEffect, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, RouteProps, useNavigate } from 'react-router-dom'
import { RootState } from 'store'
import { SettingsType } from '../../utils/hooks/useSystemSettings'

type StringRedirectMap = {
  [key in SettingsType]: string
}

const WheatRedirectOrder: Partial<StringRedirectMap> = {
  SHOW_WHEAT: '/shipments',
  SHOW_FLOURS: '/flours',
}

const BakerModuleFallbackUrl = '/products'

type ProtectedRoute = {
  settingsKey?: SettingsType
  children?: ReactNode
} & RouteProps

export const SettingsRouteGuard: React.FC<ProtectedRoute> = ({
  settingsKey,
  children,
}) => {
  const navigate = useNavigate()
  const settings = useSelector((state: RootState) => state.user.settings)
  const publicSettings = useSelector(
    (state: RootState) => state?.user?.settings || {}
  )

  const settingsReady = settingsKey
    ? settings?.[settingsKey] !== undefined
    : true

  const settingsOk = settingsKey && settings?.[settingsKey] === true

  useEffect(() => {
    // Check if we're trying to access a Wheat route while in Baker mode
    if (settings?.SHOW_BAKER_MODULE === true && settingsKey) {
      // If we're trying to access a route that requires SHOW_WHEAT or SHOW_FLOURS
      if (Object.keys(WheatRedirectOrder).includes(settingsKey)) {
        navigate(BakerModuleFallbackUrl, { replace: true })
        return
      }
    }

    if (Object.keys(publicSettings).length === 0) return
    if (settingsReady && !settingsOk) {
      for (const [fallbackKey, fallbackPath] of Object.entries(
        WheatRedirectOrder
      )) {
        if (
          fallbackKey !== settingsKey &&
          settings?.[fallbackKey as SettingsType]
        ) {
          navigate(fallbackPath, { replace: true })
          return
        }
      }

      navigate('/no-permission', { replace: true })
    }
  }, [
    settingsReady,
    settingsOk,
    navigate,
    settingsKey,
    settings,
    publicSettings,
  ])

  if (!settingsReady || !settingsOk) return null

  return children ? <>{children}</> : <Outlet />
}
